import { TenantMember, TenantSubscriptionPlan } from 'interfaces';
import FeatureToggleService from 'services/FeatureToggleService';
import { AgentPlanService } from 'services/AgentPlanService';
import { BillingMetrics, UNLIMITED_PLAN_METRIC } from 'shared/BillingMetrics';
import { PIPELINE_SCREENS_ENABLED, UNLIMITED_AGENT_CONSUMPTION } from 'shared/FeatureToggleKeys';

// Mocked values that will probably come from backend
const roles = {
  admin: {
    'tenant-summary': ['read', 'write'],
    'tenant-members': ['read', 'write'],
    'tenant-workspace': ['read', 'write'],
    'tenant-dashboard': ['read', 'write'],
    'tenant-billing': ['read'],
    'tenant-permissions-group': ['read', 'write'],
  },
  member: {
    'tenant-summary': ['read'],
    'tenant-members': [],
    'tenant-workspace': ['read', 'write'],
    'tenant-dashboard': [],
    'tenant-billing': [],
    'tenant-permissions-group': [],
  },
  guest: {
    'tenant-summary': ['read'],
    'tenant-members': [],
    'tenant-workspace': ['read'],
    'tenant-dashboard': [],
    'tenant-billing': [],
    'tenant-permissions-group': [],
  },
};
// END Mocked values that will probably come from backend

export const accessPermission = {
  summary: 'tenant-summary',
  members: 'tenant-members',
  workspace: 'tenant-workspace',
  dashboard: 'tenant-dashboard',
  billing: 'tenant-billing',
  permissionsGroup: 'tenant-permissions-group',
};

export const canUserRead = (permissionId: string, role: string): boolean => {
  if (!roles[role][permissionId]) {
    return false;
  }
  return roles[role][permissionId].indexOf('read') !== -1;
};

export const canAccessKnowledgeBase = async (permission: string, role: string, tenantId?: string): Promise<boolean> => {
  const hasReadPermission = canUserWrite(permission, role);
  if (!hasReadPermission) return false;

  const hasAgentPlan = tenantId ? await AgentPlanService.isAgentPlanActive(tenantId) : false;

  return hasAgentPlan;
};

export const canAccessPipeline = async (permission: string, role: string, tenantId?: string): Promise<boolean> => {
  const hasReadPermission = canUserRead(permission, role);
  if (!hasReadPermission) return false;

  const hasFeatureToggle = (await FeatureToggleService.isFeatureToggleEnabled(PIPELINE_SCREENS_ENABLED)) || false;
  const hasAgentPlan = tenantId ? await AgentPlanService.isAgentPlanActive(tenantId) : false;

  return hasFeatureToggle || hasAgentPlan;
};

export const canUserWrite = (permissionId, role) => {
  if (!roles[role][permissionId]) {
    return false;
  }
  return roles[role][permissionId].indexOf('write') !== -1;
};

export const hasPermissions = async (
  subscriptionMetrics: Array<TenantSubscriptionPlan>,
  loggedUser: TenantMember,
  accessPermission: string,
  metricIds: Array<BillingMetrics>,
  featureToggleId: Array<any> = [],
  usingAllEnabledMetricsValidationType = false
): Promise<boolean> => {
  if (!subscriptionMetrics) {
    return false;
  }

  if (!loggedUser) {
    return false;
  }

  const isAdminUser = canUserRead(accessPermission, loggedUser.roleId);

  const enablePage = usingAllEnabledMetricsValidationType
    ? await allEnabledMetrics(subscriptionMetrics, metricIds)
    : await hasEnabledMetric(subscriptionMetrics, metricIds);

  if (!featureToggleId.length) {
    return isAdminUser && enablePage;
  }

  const featureTogglesEnabled = featureToggleId.map(async ({ key: featureToggleKey, isFeatureToggleByCluster = false }) => {
    let isFeatureToggleEnabled = false;

    if (isFeatureToggleByCluster) {
      isFeatureToggleEnabled = featureToggleKey
        ? await FeatureToggleService.isFeatureToggleEnabledByCluster(featureToggleKey)
        : false;
    } else {
      isFeatureToggleEnabled = featureToggleKey
        ? await FeatureToggleService.isFeatureToggleEnabled(featureToggleKey)
        : false;
    }

    return usingAllEnabledMetricsValidationType
      ? isAdminUser && isFeatureToggleEnabled && enablePage
      : isAdminUser && (isFeatureToggleEnabled || enablePage);
  });

  const results = await Promise.all(featureTogglesEnabled);
  return results.every(Boolean);
};

export const hasEnabledMetric = async (
  subscriptionMetrics: Array<TenantSubscriptionPlan>,
  metricIds: Array<BillingMetrics>,
) => {
  const featureToggleId = await FeatureToggleService.isFeatureToggleEnabled(UNLIMITED_AGENT_CONSUMPTION);
  return subscriptionMetrics.some((metric) =>
    metricIds.some((metricId) =>
      metricId === BillingMetrics.agent && metric.quantity === UNLIMITED_PLAN_METRIC
        ? metric.metricId === metricId && featureToggleId
        : metric.metricId === metricId,
    ),
  );
};

export const allEnabledMetrics = async (
  subscriptionMetrics: Array<TenantSubscriptionPlan>,
  metricIds: Array<BillingMetrics>,
) => {
  const featureToggleId = await FeatureToggleService.isFeatureToggleEnabled(UNLIMITED_AGENT_CONSUMPTION);
  return metricIds.every((metricId) => {
    return subscriptionMetrics.some((metric) =>
      metricId === BillingMetrics.agent && metric.quantity === UNLIMITED_PLAN_METRIC
        ? metric.metricId === metricId && featureToggleId
        : metric.metricId === metricId,
    );
  });
};
